import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError, invoicePayApi, paymentMethodsApi, type ChargeView, type InvoicePaymentsView } from '../api/client';
import { fullWhen } from '../lib/format';
import type { Tone } from '../lib/format';
import { useData } from '../lib/useData';
import { Button, Card, Chip, LoadError, Loading, Notice } from './ui';

/**
 * Paying an invoice (1.13.0, Teampace 3091): Pay now with a saved card or bank account, or on the
 * payment processor's own page (Teampace's card plugin) with a new one, and every attempt with its
 * result. Card numbers never reach Stitch. Until Stitch takes payments, it says to pay as before.
 */

const money = (cents: number) => `${cents < 0 ? '−' : ''}$${(Math.abs(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATE: Record<ChargeView['state'], { label: string; tone: Tone }> = {
  paid: { label: 'Paid', tone: 'ok' },
  failed: { label: 'Didn’t go through', tone: 'crit' },
  processing: { label: 'Going through', tone: 'brand' },
  not_charged: { label: 'Not charged', tone: 'warn' },
  refunded: { label: 'Refunded', tone: 'neutral' }
};

export function PayInvoiceCard({ domain, invoiceId, onPaid }: { domain: string; invoiceId: string; onPaid: () => void }) {
  const data = useData(() => invoicePayApi.view(domain, invoiceId), [domain, invoiceId], { live: false });
  const methods = useData(() => paymentMethodsApi.list(domain), [domain], { live: false });
  const [view, setView] = useState<InvoicePaymentsView | null>(null);
  const [choice, setChoice] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: Tone; title: string; text: string } | null>(null);
  const back = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('payment') === 'done';
  const v = view ?? data.data;

  if (data.error && !v) {
    return (
      <Card title="Payment">
        <LoadError error={data.error} retry={data.reload} />
      </Card>
    );
  }
  if (!v) {
    return (
      <Card title="Payment">
        <Loading label="Loading payment" />
      </Card>
    );
  }
  const saved = methods.data?.available ? methods.data.methods.filter((m) => !m.expired) : [];
  const picked = saved.find((m) => String(m.id) === choice) ?? saved.find((m) => m.default) ?? saved[0];

  const pay = async () => {
    if (!picked) return;
    setBusy(true);
    setSaid(null);
    try {
      const r = await invoicePayApi.pay(domain, invoiceId, picked.id);
      setView(r.payments);
      const c = r.charge;
      setSaid(
        c.state === 'paid'
          ? { tone: 'ok', title: 'Paid. Thank you.', text: `${money(c.amountCents)} with ${c.card}. A receipt is on its way to the people who get invoices.` }
          : c.state === 'failed'
            ? { tone: 'crit', title: 'That didn’t go through', text: `${c.reason ?? 'The payment was declined.'} Try another card or a bank account.` }
            : c.state === 'not_charged'
              ? { tone: 'warn', title: 'Not charged', text: c.reason ?? 'Nothing was charged. Try again later.' }
              : { tone: 'brand', title: 'Going through', text: 'It’s being confirmed. This page shows the result in a minute or two; nothing is charged twice.' }
      );
      if (c.state === 'paid') onPaid();
    } catch (err) {
      setSaid({ tone: 'crit', title: 'That didn’t work', text: err instanceof ApiError ? err.message : 'Try again in a minute.' });
    } finally {
      setBusy(false);
    }
  };

  const page = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const { url } = await invoicePayApi.page(domain, invoiceId);
      window.location.assign(url);
    } catch (err) {
      setSaid({ tone: 'crit', title: 'That didn’t work', text: err instanceof ApiError ? err.message : 'The payment page couldn’t be opened. Try again.' });
      setBusy(false);
    }
  };

  return (
    <Card title="Payment" action={v.openCents > 0 ? <span className="muted small">{money(v.openCents)} to pay</span> : <Chip tone="ok" small>Nothing to pay</Chip>}>
      {back && v.openCents > 0 && <Notice tone="brand">Back from the payment page. If you paid, it shows here in a minute or two.</Notice>}
      {v.openCents > 0 && !v.canPay && v.reason && <p className="muted">{v.reason}</p>}
      {v.canPay && (
        <>
          {saved.length > 0 ? (
            <div className="pay-row">
              {saved.length > 1 ? (
                <div className="field grow">
                  <label htmlFor="pay-with">Pay with</label>
                  <select id="pay-with" className="input" value={String(picked?.id ?? '')} onChange={(e) => setChoice(e.target.value)} disabled={busy}>
                    {saved.map((m) => (
                      <option key={m.id} value={String(m.id)}>
                        {m.label}
                        {m.default ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <span className="grow">
                  With <strong>{picked?.label}</strong>
                </span>
              )}
              <Button variant="primary" disabled={busy || !picked} onClick={() => void pay()}>
                Pay {money(v.openCents)} now
              </Button>
            </div>
          ) : (
            <p className="muted">No card or bank account is saved. Pay on the payment page, or add one under Billing → Payment methods.</p>
          )}
          <div className="billing-actions">
            <Button variant={saved.length ? 'ghost' : 'primary'} disabled={busy} onClick={() => void page()}>
              Pay with a new card or bank account
            </Button>
          </div>
          <p className="muted small">Card numbers are kept by Ceexo’s payment processor, never by Stitch.</p>
        </>
      )}
      {said && (
        <Notice tone={said.tone} title={said.title}>
          {said.text}
        </Notice>
      )}
      {v.attempts.length > 0 && (
        <div className="table-wrap">
          <table className="table compact">
            <thead>
              <tr>
                <th>When</th>
                <th>How</th>
                <th>Card</th>
                <th className="num">Amount</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {v.attempts.map((a) => (
                <tr key={a.id}>
                  <td>{fullWhen(a.at)}</td>
                  <td>{a.how}</td>
                  <td>{a.card || '—'}</td>
                  <td className="num">{money(a.amountCents)}</td>
                  <td>
                    <Chip tone={STATE[a.state].tone} small>
                      {STATE[a.state].label}
                    </Chip>
                    {a.reason && <span className="muted small"> {a.reason}</span>}
                    {a.refundedCents > 0 && a.state !== 'refunded' && <span className="muted small"> {money(a.refundedCents)} refunded</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/**
 * For the company's admins (and on Billing): an invoice whose payment failed, or that is past due,
 * with what's blocked and a way to pay. Nothing shows for someone who can't see billing.
 */
export function PaymentBanner({ domain, inline = false }: { domain: string; inline?: boolean }) {
  const data = useData(() => invoicePayApi.status(domain), [domain], { live: false });
  const b = data.data;
  if (!b?.on || !b.unpaid.length) return null;
  const first = b.unpaid[0]!;
  const total = b.unpaid.reduce((n, u) => n + u.openCents, 0);
  const to = `/t/${encodeURIComponent(domain)}/billing/invoices/${encodeURIComponent(first.invoiceId)}`;
  const title = b.suspended ? 'Your account is suspended until an invoice is paid' : b.blocked ? 'An invoice is unpaid: new people and numbers can’t be added' : first.why === 'failed' ? 'A payment didn’t go through' : 'An invoice is past due';
  return (
    <div className={`payment-banner${inline ? ' inline' : ''}`}>
      <Notice tone={b.blocked || b.suspended ? 'crit' : 'warn'} title={title}>
        {b.unpaid.length === 1 ? `Invoice ${first.number}: ${money(first.openCents)} to pay.` : `${b.unpaid.length} invoices: ${money(total)} to pay.`}
        {first.why === 'failed' && first.reason ? ` ${first.reason}` : ''}
        {first.nextTry ? ` We’ll try the card again ${first.nextTry}.` : ''}
        {b.suspended ? ' Calls go on, and 911 always works.' : ''} <Link to={to}>Pay now</Link>
      </Notice>
    </div>
  );
}
